import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const PAGE = 20;        // messages fetched per request
const MAX_WINDOW = 60;  // messages kept in the page at once; the far end is dropped and reloaded on scroll

/**
 * Windowed chat history with scrolling in both directions.
 *
 *  - opens on the latest PAGE messages, scrolled to the bottom
 *  - scrolling to the top loads the PAGE older messages (scroll position is kept)
 *  - scrolling to the bottom loads the PAGE newer ones, if the window was trimmed
 *  - the window never grows past MAX_WINDOW, so very long chats stay light
 *
 * `fetchPage(params)` must resolve to { items, hasOlder, hasNewer } where every item has
 * a unique `idx` (position in the thread) — see the server's utils/chatWindow.js.
 * `chatKey` identifies the open conversation (changing it reloads), and `version` is any
 * value that changes when the conversation gets new messages (triggers a quiet refresh).
 */
export default function useChatWindow({ fetchPage, chatKey, version }) {
    const [state, setState] = useState({ items: [], hasOlder: false, hasNewer: false });
    const [loading, setLoading] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [loadingNewer, setLoadingNewer] = useState(false);

    const scrollRef = useRef(null);
    const live = useRef(state);          // latest state for async callbacks
    const token = useRef(0);             // bumps on every reload, so stale responses are ignored
    const busy = useRef({ older: false, newer: false });
    const pendingScroll = useRef(null);  // what to do with the scroll position after the next render
    const fetchRef = useRef(fetchPage);
    const versionRef = useRef(version);
    fetchRef.current = fetchPage;

    const commit = useCallback((next) => { live.current = next; setState(next); }, []);

    // The first message in view and where it sits, so it can be put back after the list changes.
    const captureAnchor = () => {
        const el = scrollRef.current;
        if (!el) return null;
        const rows = el.querySelectorAll('[data-chat-id]');
        for (const row of rows) {
            if (row.offsetTop + row.offsetHeight > el.scrollTop) return { id: row.dataset.chatId, offset: row.offsetTop - el.scrollTop };
        }
        return null;
    };

    const load = useCallback(async () => {
        const mine = ++token.current;
        busy.current = { older: false, newer: false };
        commit({ items: [], hasOlder: false, hasNewer: false });
        setLoading(true);
        try {
            const page = await fetchRef.current({ limit: PAGE });
            if (mine !== token.current) return;
            pendingScroll.current = { type: 'bottom' };
            commit({ items: page.items, hasOlder: page.hasOlder, hasNewer: page.hasNewer });
        } catch (err) { /* the page shows its own toast */ }
        if (mine === token.current) setLoading(false);
    }, [commit]);

    const loadOlder = useCallback(async () => {
        const cur = live.current;
        if (busy.current.older || !cur.hasOlder || !cur.items.length) return;
        busy.current.older = true;
        setLoadingOlder(true);
        const mine = token.current;
        const anchor = captureAnchor();
        try {
            const page = await fetchRef.current({ limit: PAGE, before: cur.items[0].idx });
            if (mine !== token.current) return;
            let items = [...page.items, ...live.current.items];
            let hasNewer = live.current.hasNewer;
            if (items.length > MAX_WINDOW) { items = items.slice(0, MAX_WINDOW); hasNewer = true; }
            pendingScroll.current = anchor ? { type: 'anchor', ...anchor } : null;
            commit({ items, hasOlder: page.hasOlder, hasNewer });
        } catch (err) { /* ignore */ }
        busy.current.older = false;
        setLoadingOlder(false);
    }, [commit]);

    const loadNewer = useCallback(async () => {
        const cur = live.current;
        if (busy.current.newer || !cur.hasNewer || !cur.items.length) return;
        busy.current.newer = true;
        setLoadingNewer(true);
        const mine = token.current;
        const anchor = captureAnchor();
        try {
            const page = await fetchRef.current({ limit: PAGE, after: cur.items[cur.items.length - 1].idx });
            if (mine !== token.current) return;
            let items = [...live.current.items, ...page.items];
            let hasOlder = live.current.hasOlder;
            if (items.length > MAX_WINDOW) { items = items.slice(-MAX_WINDOW); hasOlder = true; }
            pendingScroll.current = anchor ? { type: 'anchor', ...anchor } : null;
            commit({ items, hasOlder, hasNewer: page.hasNewer });
        } catch (err) { /* ignore */ }
        busy.current.newer = false;
        setLoadingNewer(false);
    }, [commit]);

    // Pull in messages that arrived since the last fetch. `stick` forces a jump to the newest message.
    const refreshTail = useCallback(async ({ stick = false } = {}) => {
        const cur = live.current;
        if (!cur.items.length) { await load(); return; }
        if (cur.hasNewer) { if (stick) await load(); return; } // reading older history — don't yank the view

        const el = scrollRef.current;
        const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        const mine = token.current;
        let items = cur.items;
        let hasOlder = cur.hasOlder;
        let more = true;
        let added = false;
        try {
            for (let i = 0; i < 5 && more; i += 1) {
                const page = await fetchRef.current({ limit: PAGE, after: items[items.length - 1].idx });
                if (mine !== token.current) return;
                if (!page.items.length) break;
                items = [...items, ...page.items];
                added = true;
                more = page.hasNewer;
            }
        } catch (err) { return; }
        if (!added) return;
        if (items.length > MAX_WINDOW) { items = items.slice(-MAX_WINDOW); hasOlder = true; }
        pendingScroll.current = stick || nearBottom ? { type: 'bottom' } : null;
        commit({ items, hasOlder, hasNewer: more });
    }, [commit, load]);

    const jumpToLatest = useCallback(() => refreshTail({ stick: true }), [refreshTail]);

    // ── In-place edits (reactions, edits, deletes, pins, stars) ──
    const sameItem = (item, id) => item.id === id || item._id === id;
    const patchItem = useCallback((id, patch) => {
        const cur = live.current;
        commit({ ...cur, items: cur.items.map(item => (sameItem(item, id) ? { ...item, ...patch } : item)) });
    }, [commit]);
    const removeItem = useCallback((id) => {
        const cur = live.current;
        commit({ ...cur, items: cur.items.filter(item => !sameItem(item, id)) });
    }, [commit]);

    // Re-read exactly the messages on screen (someone reacted / edited / deleted).
    const refreshInPlace = useCallback(async () => {
        const cur = live.current;
        if (!cur.items.length) return;
        const mine = token.current;
        try {
            const last = cur.items[cur.items.length - 1].idx;
            const span = last - cur.items[0].idx + 1;
            const page = await fetchRef.current({ limit: Math.min(Math.max(span, PAGE), 100), before: last + 1 });
            if (mine !== token.current) return;
            pendingScroll.current = null;
            commit({ ...live.current, items: page.items });
        } catch (err) { /* ignore */ }
    }, [commit]);

    // Restore / set the scroll position once the new list is on screen, and fill short lists.
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const action = pendingScroll.current;
        pendingScroll.current = null;
        if (action?.type === 'bottom') {
            el.scrollTop = el.scrollHeight;
        } else if (action?.type === 'anchor') {
            const row = el.querySelector(`[data-chat-id="${CSS.escape(action.id)}"]`);
            if (row) el.scrollTop = row.offsetTop - action.offset;
        }
        // Not enough messages to scroll yet? Keep fetching until the pane fills or the history ends.
        if (state.items.length && el.scrollHeight <= el.clientHeight + 40) {
            if (state.hasOlder) loadOlder();
            else if (state.hasNewer) loadNewer();
        }
    }, [state, loadOlder, loadNewer]);

    useEffect(() => {
        versionRef.current = version;
        if (chatKey) load();
        else { token.current += 1; commit({ items: [], hasOlder: false, hasNewer: false }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatKey]);

    useEffect(() => {
        if (!chatKey || version === versionRef.current) return;
        versionRef.current = version;
        refreshInPlace().then(() => refreshTail());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version]);

    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollTop < 80) loadOlder();
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadNewer();
    }, [loadOlder, loadNewer]);

    return {
        items: state.items, hasOlder: state.hasOlder, hasNewer: state.hasNewer,
        loading, loadingOlder, loadingNewer,
        scrollRef, onScroll, jumpToLatest, refreshTail, refreshInPlace, patchItem, removeItem, reload: load,
    };
}
